/**
 * Modal to view completed assessment answers (answer sheet) and download PDF.
 * Uses GET .../run/answer-sheet?runId= and GET .../run/answer-sheet/pdf?runId=.
 * See docs/portals/practitioner-assessment-answer-sheet-modal.md
 */
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAnswerSheet, getAnswerSheetPdf } from 'src/mentor/services/practitionerAssessmentService'

function errorMessageFromCode(code, defaultMsg) {
  if (code === 4038) return 'Answer sheet will be available after assessment completion.'
  if (code === 4030) return 'You do not have access to this answer sheet.'
  if (code === 4040) return 'Run not found. Refresh the list and try again.'
  return defaultMsg || 'Something went wrong.'
}

// Match runner option palette so answer-sheet columns visually map to option scales.
const OPTION_COLUMN_PALETTE = ['error.main', 'warning.dark', 'warning.main', 'success.light', 'success.main']

function getOptionColumnColor(theme, index, total) {
  if (!theme?.palette || total <= 1) return theme?.palette?.primary?.main || '#1976d2'
  const step = index / (total - 1)
  const paletteIndex = Math.round(step * (OPTION_COLUMN_PALETTE.length - 1))
  const key = OPTION_COLUMN_PALETTE[paletteIndex]
  const [area, shade] = String(key).split('.')
  return theme.palette?.[area]?.[shade] ?? theme.palette?.primary?.main ?? '#1976d2'
}

export default function PractitionerAssessmentAnswerSheetModal({ open, runId, onClose }) {
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [sheet, setSheet] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!runId || !open) return
    setLoading(true)
    setError(null)
    setSheet(null)
    try {
      const data = await getAnswerSheet(runId)
      if (data?.error) {
        setError({
          code: data.error.code,
          message: errorMessageFromCode(data.error.code, data.error.message),
        })
        setSheet(null)
      } else {
        setSheet(data?.assessmentAnswerSheet ?? null)
        setError(null)
      }
    } catch (e) {
      setError({ message: 'Failed to load answer sheet.' })
      setSheet(null)
    } finally {
      setLoading(false)
    }
  }, [runId, open])

  useEffect(() => {
    if (open && runId) load()
  }, [open, runId, load])

  const handleDownloadPdf = useCallback(async () => {
    if (!runId) return
    setDownloading(true)
    try {
      const result = await getAnswerSheetPdf(runId)
      if (result?.error) {
        const msg = errorMessageFromCode(result.error.code, result.error.message)
        toast.error(msg)
        return
      }
      if (result?.blob && result?.filename) {
        const url = URL.createObjectURL(result.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.filename
        link.click()
        URL.revokeObjectURL(url)
        toast.success('Download started')
      }
    } catch (e) {
      toast.error('Failed to download PDF')
    } finally {
      setDownloading(false)
    }
  }, [runId])

  const handleClose = useCallback(() => {
    setSheet(null)
    setError(null)
    onClose?.()
  }, [onClose])

  const questions = sheet?.questions ?? []
  const optionColumnsHeader = questions.find((q) => Array.isArray(q.optionColumns) && q.optionColumns.length > 0)?.optionColumns ?? []
  const meta = sheet
    ? {
        assessmentTitle: sheet.assessmentTitle,
        clinicalAssessmentTitle: sheet.clinicalAssessmentTitle,
        patientName: sheet.patientName,
        completedAt: sheet.completedAt,
      }
    : null

  /** API may return completedAt as Unix seconds; JS Date expects milliseconds. */
  const completedDate =
    meta?.completedAt != null
      ? typeof meta.completedAt === 'number'
        ? new Date(meta.completedAt * 1000)
        : new Date(meta.completedAt)
      : null
  const completedLabel =
    completedDate && !Number.isNaN(completedDate.getTime())
      ? completedDate.toLocaleString()
      : null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          View Answers
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {sheet && (
            <Button
              size="small"
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Downloading…' : 'Download Answers'}
            </Button>
          )}
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 200 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="error">{error.message}</Typography>
          </Box>
        )}
        {!loading && !error && meta && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {meta.clinicalAssessmentTitle && `${meta.clinicalAssessmentTitle} · `}
              {meta.assessmentTitle}
            </Typography>
            {meta.patientName && (
              <Typography variant="body2" color="text.secondary">
                Patient: {meta.patientName}
              </Typography>
            )}
            {completedLabel && (
              <Typography variant="caption" color="text.secondary">
                Completed: {completedLabel}
              </Typography>
            )}
          </Box>
        )}
        {!loading && !error && questions.length > 0 && (
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider', py: 1, px: 1.5 }, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, borderBottom: 2, borderColor: 'divider', minWidth: 160 }}>
                  Questions
                </TableCell>
                {optionColumnsHeader.map((col) => (
                  <TableCell
                    key={col.optionId}
                    align="center"
                    sx={(theme) => {
                      const colIndex = optionColumnsHeader.findIndex((c) => c.optionId === col.optionId)
                      const accent = getOptionColumnColor(theme, Math.max(colIndex, 0), optionColumnsHeader.length)
                      return {
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        borderBottom: 2,
                        borderColor: 'divider',
                        width: 56,
                        bgcolor: alpha(accent, 0.14),
                      }
                    }}
                  >
                    {col.optionLabel ?? col.optionId}
                  </TableCell>
                ))}
                {optionColumnsHeader.length === 0 && (
                  <TableCell align="center" sx={{ fontWeight: 600, borderBottom: 2, borderColor: 'divider' }}>
                    Answer
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((q, idx) => {
                const hasOptions = Array.isArray(q.optionColumns) && q.optionColumns.length > 0
                const checkedByOptionId = hasOptions
                  ? new Map(q.optionColumns.map((c) => [c.optionId, c.checked]))
                  : new Map()
                const questionLabel = q.question ?? q.shortQuestion ?? `Question ${idx + 1}`
                return (
                  <TableRow key={q.questionId ?? q.questionRefId ?? idx}>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                      <Typography variant="body2" component="span">
                        {questionLabel}
                      </Typography>
                    </TableCell>
                    {optionColumnsHeader.length > 0 ? (
                      optionColumnsHeader.map((col, colIndex) => (
                        <TableCell
                          key={col.optionId}
                          align="center"
                          sx={(theme) => {
                            const accent = getOptionColumnColor(theme, colIndex, optionColumnsHeader.length)
                            return {
                              verticalAlign: 'middle',
                              bgcolor: alpha(accent, 0.1),
                            }
                          }}
                        >
                          <Box
                            sx={(theme) => {
                              const accent = getOptionColumnColor(theme, colIndex, optionColumnsHeader.length)
                              const checked = Boolean(checkedByOptionId.get(col.optionId))
                              return {
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: checked ? accent : alpha(accent, 0.45),
                                bgcolor: checked ? accent : alpha(accent, 0.06),
                                mx: 'auto',
                              }
                            }}
                          />
                        </TableCell>
                      ))
                    ) : (
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {q.freeTextAnswer != null && String(q.freeTextAnswer).trim() !== '' ? (
                          <Typography variant="body2" whiteSpace="pre-wrap">
                            {q.freeTextAnswer}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        {!loading && !error && sheet && questions.length === 0 && (
          <Typography color="text.secondary">No questions in this answer sheet.</Typography>
        )}
      </DialogContent>
    </Dialog>
  )
}
